"use memo"

import {
  ReactNode,
  useContext,
  useEffect,
  useRef
} from 'react'
import { ConnectionState, NotificationEvents } from 'types/pushNotifications'
import {
  receiveNotifications,
  stopNotifications,
} from 'utils/signalrConnector'
import { AppAlert } from 'components/app-alert/app-alert'
import InitialiseUser from 'services/functions/campaigns/initialiseUser'
import { AuthContext } from '@urbanx/agx.auth.client'
import useBrowserLock from 'hooks/useBrowserLock'
import useHubConnection from 'hooks/useHubConnection'

const getJoinedId = (id?: (string | undefined)[]) => {
  if (!id || id.length === 0) return undefined
  return id.filter((i) => i !== undefined).join('-')
}

export interface HubEventHandler {
  eventType: string
  handler: (event: NotificationEvents) => void
  id?: (string | undefined)[]
}

interface SetupNotificationsLayoutProps {
  hubName: string
  children: ReactNode
  eventHandlers?: HubEventHandler[]
  onConnected?: () => void
  onDisconnected?: () => void
}

export const SetupNotificationsLayout = ({
  hubName,
  children,
  eventHandlers = [],
  onConnected,
  onDisconnected,
}: SetupNotificationsLayoutProps) => {
  const registeredEventsRef = useRef<Set<string>>(new Set())
  const { getAuthToken } = useContext(AuthContext)
  const eventHandlerRefsRef = useRef<Map<string, (...events: NotificationEvents[]) => void>>(new Map())

  useBrowserLock()

  const hubInfo = useHubConnection(hubName)

  useEffect(() => {
    const connection = hubInfo.hubConnection
    if (
      !connection ||
      hubInfo.connectionState !== ConnectionState.Connected ||
      eventHandlers.length === 0
    ) {
      return
    }

    eventHandlers.forEach((handler) => {
      const eventId = getJoinedId(handler.id)
      const registrationKey = `${hubName}-${handler.eventType}${eventId ? `-${eventId}` : ''}`

      if (registeredEventsRef.current.has(registrationKey))
        return

      const wrappedHandler = (...events: NotificationEvents[]) => {
        events.forEach((event) => handler.handler(event))
      }

      eventHandlerRefsRef.current.set(registrationKey, wrappedHandler)

      receiveNotifications(
        connection,
        handler.eventType,
        wrappedHandler
      )

      registeredEventsRef.current.add(registrationKey)
    })

    // Cleanup function to unregister handlers
    return () => {
      if (!connection) return

      eventHandlers.forEach((handler) => {
        const eventId = getJoinedId(handler.id)
        const registrationKey = `${hubName}-${handler.eventType}${eventId ? `-${eventId}` : ''}`

        const wrappedHandler = eventHandlerRefsRef.current.get(registrationKey)
        if (wrappedHandler) {
          stopNotifications(
            connection,
            handler.eventType,
            wrappedHandler
          )
          eventHandlerRefsRef.current.delete(registrationKey)
          registeredEventsRef.current.delete(registrationKey)
          console.log(`Unregistered event handler: ${registrationKey}`)
        }
      })
    }
  }, [hubInfo.hubConnection, hubInfo.connectionState, eventHandlers, hubName])

  // Initialize user on connection
  useEffect(() => {
    if (hubInfo.connectionState === ConnectionState.Connected) {
      console.log('Initializing hub user')
      InitialiseUser(getAuthToken)
    }
  }, [hubInfo.connectionState, getAuthToken])

  // Lifecycle callbacks
  useEffect(() => {
    if (hubInfo.connectionState === ConnectionState.Connected && onConnected) {
      onConnected()
    }
  }, [hubInfo.connectionState, onConnected])

  useEffect(() => {
    if (hubInfo.connectionState === ConnectionState.Disconnected && onDisconnected) {
      onDisconnected()
    }
  }, [hubInfo.connectionState, onDisconnected])

  const appAlert =
    hubInfo.connectionState === ConnectionState.Reconnecting ? (
      <AppAlert text="Staff Portal is reconnecting..." />
    ) : hubInfo.connectionState === ConnectionState.Disconnected ? (
      <AppAlert text="Staff Portal disconnected. Try reload the web page." />
    ) : null

  if (!hubName) {
    console.warn('No hub name provided for SetupNotificationsLayout')
    return <>{children}</>
  }

  return (
    <>
      {appAlert}
      {children}
    </>
  )
}
